import React, { cloneElement, createContext, useContext, useEffect, useMemo, useState } from 'react';
import generated from './es.json';
import overrides from './overrides.json';
import { getLanguage, saveLanguage, syncLanguageStorage, observeLanguage } from './language';

const Context = createContext({ language: 'en', setLanguage: saveLanguage });
const dictionary = { ...generated, ...overrides };
const folded = new Map(Object.entries(dictionary).map(([key,value]) => [key.toLowerCase(),value]));
const escaped = text => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const patterns = Object.entries(dictionary).filter(([key]) => /\{\d+\}/.test(key) && key.replace(/\{\d+\}/g,'').trim().length >= 4).map(([key,value]) => {
  const indexes = [];
  const parts = key.split(/(\{\d+\})/g).map(part => {
    if (/^\{\d+\}$/.test(part)) { indexes.push(part); return '([\\s\\S]+?)'; }
    return escaped(part);
  });
  return { regex: new RegExp('^'+parts.join('')+'$'), value, indexes };
}).sort((a,b) => b.regex.source.length-a.regex.source.length);
export function translateText(value, language = getLanguage()) {
  if (language !== 'es' || typeof value !== 'string' || !value.trim()) return value;
  const key = value.replace(/\s+/g,' ').trim();
  const prefix = value.match(/^\s*/)[0], suffix = value.match(/\s*$/)[0];
  if (Object.prototype.hasOwnProperty.call(dictionary,key)) return prefix + dictionary[key] + suffix;
  if (folded.has(key.toLowerCase())) return prefix + folded.get(key.toLowerCase()) + suffix;
  for (const pattern of patterns) {
    const match = key.match(pattern.regex);
    if (match) return prefix + pattern.value.replace(/\{\d+\}/g, item => match[pattern.indexes.indexOf(item)+1] ?? item) + suffix;
  }
  for (const separator of [' · ',' • ',' | ']) {
    if (key.includes(separator)) return prefix + key.split(separator).map(part => translateText(part,language)).join(separator) + suffix;
  }
  return value;
}
export const LanguageProvider = ({ children }) => {
  const [language, set] = useState(getLanguage);
  useEffect(() => {
    const local = e => set(observeLanguage(e.detail));
    const storage = e => { if (e.key === 'selectedLanguage' || e.key === null) set(syncLanguageStorage()); };
    window.addEventListener('stg-language',local); window.addEventListener('storage',storage);
    return () => { window.removeEventListener('stg-language',local); window.removeEventListener('storage',storage); };
  }, []);
  useEffect(() => { document.documentElement.lang = language; }, [language]);
  const value = useMemo(() => ({ language, setLanguage: saveLanguage }), [language]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
};
export const useLanguage = () => useContext(Context);
const localizedValue = (value, language) => Array.isArray(value) ? value.map(item => localizedValue(item,language)) : translateText(value,language);
export const LocaleText = ({ value }) => { const { language } = useLanguage(); return localizedValue(value,language); };
export const translateTemplate = (message, values, language = getLanguage()) => {
  const copy = language === 'es' ? dictionary[message.replace(/\s+/g,' ').trim()] : null;
  const translated = copy ? message.match(/^\s*/)[0] + copy + message.match(/\s*$/)[0] : message;
  return translated.replace(/\{(\d+)\}/g, (match,index) => values[index] == null ? '' : String(values[index]));
};
export const LocaleTemplate = ({ message, values }) => { const { language } = useLanguage(); return translateTemplate(message,values,language); };
export const LocaleProps = ({ element, names = [] }) => {
  const { language } = useLanguage();
  const props = {};
  for (const key of names)
    if (typeof element.props[key] === 'string') props[key] = translateText(element.props[key],language);
  return cloneElement(element,props);
};